import { useEffect, useState } from "react";
import { Check, Copy, Share2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAppStore } from "@/store/app";

export function ShareSessionDialog() {
  const sessionId = useAppStore((s) => s.sessionId);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Build the shareable URL from the current location so it works on
  // localhost and the deployed Web App alike.
  const shareUrl = (() => {
    if (!sessionId) return "";
    const url = new URL(window.location.href);
    url.searchParams.set("sessionId", sessionId);
    return url.toString();
  })();

  // Reset the Copied state whenever the dialog reopens.
  useEffect(() => {
    if (open) setCopied(false);
  }, [open]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      // Fallback for browsers without the async clipboard API.
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5" disabled={!sessionId}>
          <Share2 className="w-3.5 h-3.5" />
          Share session
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Share this session</DialogTitle>
          <DialogDescription>
            Anyone with this link will open the current session — its documents, agent
            state, and drafted letter.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <input
            readOnly
            value={shareUrl}
            onFocus={(e) => e.currentTarget.select()}
            className="flex-1 rounded-md border bg-muted px-3 py-2 text-sm font-mono text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <Button onClick={copy} size="sm" className="gap-1.5 shrink-0">
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5" />
                Copied
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                Copy
              </>
            )}
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          Sessions are held in memory on the server. If the server restarts, this link
          will stop working.
        </p>
      </DialogContent>
    </Dialog>
  );
}
